/**
 * Offline Toast Utility (P2.5)
 * 
 * Shows user-friendly messages when attempting unsupported offline actions.
 * Prevents support burden and aligns with the honesty rule.
 */
import { toast } from 'sonner';

// Check if we're offline
export const isOffline = () => !navigator.onLine;

// List of actions supported offline (v1)
export const OFFLINE_SUPPORTED_ACTIONS = [
  'MARK_ACTION_DONE',
];

/**
 * Show toast for unsupported offline action
 * @param {string} action - Description of what the user tried to do
 */
export const showOfflineToast = (action = 'This action') => {
  toast.warning(`${action} unavailable offline (v1)`, {
    description: 'Reconnect to continue.',
    duration: 4000,
  });
};

/**
 * Check if offline and show toast if so
 * Returns true if offline (action should be blocked)
 * @param {string} action - Description of what the user is trying to do
 * @returns {boolean} true if offline (caller should abort action)
 */
export const blockIfOffline = (action) => {
  if (isOffline()) {
    showOfflineToast(action);
    return true;
  }
  return false;
};

/**
 * Higher-order function to wrap callbacks with offline check
 * @param {Function} callback - The function to wrap
 * @param {string} action - Description of the action for the toast
 * @returns {Function} Wrapped function that checks offline first
 */
export const withOfflineCheck = (callback, action) => {
  return (...args) => {
    if (blockIfOffline(action)) {
      return;
    }
    return callback(...args);
  };
};

export default {
  isOffline,
  showOfflineToast,
  blockIfOffline,
  withOfflineCheck,
  OFFLINE_SUPPORTED_ACTIONS,
};
